'use client';

import { useEffect, useState } from 'react';
import { createClient } from '@/lib/supabase/client';
import { RecipeFilters, type RecipeFiltersProps } from './recipe-filters';

type Props = Omit<RecipeFiltersProps, 'showHashimoto'>;

/**
 * Thin wrapper around RecipeFilters that resolves the viewer's gated dietary
 * preferences (Hashimoto) client-side, so the recipes page can remove
 * `force-dynamic` and benefit from prefetching.
 *
 * Renders immediately with the flag false (no flicker for non-opted-in
 * users). If the viewer has the preference, the chip appears after the
 * lightweight profile fetch completes.
 */
type WrapperProps = Omit<Props, 'showLibrary'>;

export function RecipeFiltersWithAuth(props: WrapperProps) {
  const [showHashimoto, setShowHashimoto] = useState(false);
  // The cooked / in-cookbook filters are per-user — only signed-in viewers see
  // them (the public page stays usable for anonymous visitors).
  const [signedIn, setSignedIn] = useState(false);

  useEffect(() => {
    const supabase = createClient();
    supabase.auth.getUser().then((res: { data: { user: { id: string } | null } }) => {
      const user = res.data.user;
      if (!user) return;
      setSignedIn(true);
      supabase
        .from('profiles')
        .select('dietary_preference_slugs')
        .eq('id', user.id)
        .single()
        .then((res: { data: { dietary_preference_slugs: string[] | null } | null }) => {
          const slugs = res.data?.dietary_preference_slugs ?? [];
          setShowHashimoto(slugs.includes('hashimoto'));
        });
    });
  }, []);

  return (
    <RecipeFilters
      {...props}
      showHashimoto={showHashimoto}
      showLibrary={signedIn}
    />
  );
}
